import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog';
import BusinessCustomersActions from "../../../Redux/transition/customers/business-customer.reducer";
import { createCustomHandleApostropheChange, removeTrailingSpacesAndDashes } from "../../../Components/CleanInputs";
import { useTranslation, Trans } from "react-i18next";
import CustomToast from '../../../Components/CustomToast'
import { useSelector, useDispatch } from "react-redux";
import "../../../Styles/components/tags.scss";
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';

export const EditTag = ({
    visible,
    hideEditTagPopup,
    getSummaryCustomers,
    getTags,
    pageOptions,
    tagToEdit = null,
    tagGroupToEdit = null,
    onTagsChanged,
}) => {

    const { t } = useTranslation("common");

    const [toastTrigger, setToastTrigger] = useState(false);
    const [tagValues, setTagValues] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [updatedName, setUpdatedName] = useState("");
    const [tagGroupValues, setTagGroupValues] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({ tag: "" });
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [finalName, setFinalName] = useState("");
    const [sessionFromList, setSessionFromList] = useState(false);

    const [state, setState] = useState({
        updating: false
    })

    const resetDialogState = useCallback(() => {
        setTagValues(null);
        setTagGroupValues(null);
        setUpdatedName("");
        setFinalName("");
        setFieldErrors({ tag: "" });
        setFormData({});
        setShowErrorMessage(false);
        setSessionFromList(false);
        setState((prev) => ({ ...prev, updating: false }));
    }, []);

    const [tagToast, setTagToast] = useState({
        severity: "",
        summary: "",
        detail: "",
        life: 0
    });

    const showTagToast = (severity, summary, detail) => {
        setTagToast({
            severity,
            summary,
            detail,
            life: 4000,
        });
        setToastTrigger(true);
    }

    // eslint-disable-next-line no-unused-vars
    const [formData, setFormData] = useState({
        name: tagValues?.name,
    });

    //redux state from store
    const { updatingTag, updatedTag, updateTagError, tagGroupsGraphed } = useSelector((state) => state.businessCustomers);
    const defMembership = useSelector((state) => state.defMembership.defMembership);

    const editTagErrorMessage = updateTagError?.detail?.split('"')[1] ? updateTagError?.detail?.split('"')[1] : updateTagError?.detail;

    const dispatch = useDispatch();
    const updateTag = useCallback((tagId, tag) => dispatch(BusinessCustomersActions.updateTagRequest(tagId, tag)), [dispatch]);
    const getTagGroupsGraphed = useCallback((businessId) => dispatch(BusinessCustomersActions.tagGroupGraphedRequest(businessId)), [dispatch]);

    useEffect(() => {
        if (!updatingTag && state.updating && updatedTag) {
            showTagToast("success", t("tags.success"), t("tags.tag_edit_success"))
        }
        if (!updatingTag && state.updating && updateTagError) {
            showTagToast("error", t("tags.error"), editTagErrorMessage)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateTagError, updatingTag, state.updating, updatedTag]);

    useEffect(() => {
        if (tagValues) {
            setUpdatedName(tagValues?.name)
        }
    }, [tagValues])

    useEffect(() => {
        if (!visible) {
            resetDialogState();
            return;
        }
        if (defMembership?.business?.id) {
            getTagGroupsGraphed(defMembership.business.id);
        }
        if (tagToEdit && tagGroupToEdit) {
            setSessionFromList(Boolean(tagToEdit.id));
            setTagValues(tagToEdit);
            setTagGroupValues(tagGroupToEdit);
            setFinalName(tagToEdit.name ?? "");
            setUpdatedName(tagToEdit.name ?? "");
        }
    }, [visible, tagToEdit, tagGroupToEdit, defMembership?.business?.id, getTagGroupsGraphed, resetDialogState]);

    const showCompactEdit = sessionFromList || Boolean(tagToEdit?.id);

    const tagGroupOptions = useMemo(() => {
        if (!tagGroupsGraphed?.groups) {
            return [];
        }
        return tagGroupsGraphed.groups.filter((group) => group.name && group.id);
    }, [tagGroupsGraphed]);

    const tagOptions = useMemo(() => {
        if (!tagGroupValues) {
            return [];
        }
        const tags = tagGroupValues.tags ?? (tagValues?.id ? [tagValues] : []);
        return tags.filter((tag) => tag.name && tag.id);
    }, [tagGroupValues, tagValues]);

    const onDropdownChange = (e) => {
        const raw = e?.value;

        if (typeof raw === "string") {
            setUpdatedName(raw);
            const { isValid } = createCustomHandleApostropheChange(raw);
            setFieldErrors(prev => ({
                ...prev,
                tag: isValid ? "" : t("common.allow_characters")
            }));
        } else {
            setUpdatedName(raw.name);
            setTagValues(raw);

            setFieldErrors(prev => ({ ...prev, tag: "" }));
        }
    };

    const onSubmit = () => {
        const tagId = tagValues?.id;
        const tag = {
            name: removeTrailingSpacesAndDashes(finalName),
            business: {
                id: defMembership.business.id
            },
            id: tagId,
            group: { id: tagGroupValues.id }
        };

        setState(s => ({ ...s, updating: true }));
        updateTag(tagId, tag);
        setFinalName(tag.name);
    };

    const handleInput = (e) => {
        const value = e.target.value;
        setFinalName(value);
        const handleValidation = createCustomHandleApostropheChange(
            { setFieldValue: (field, val) => setFormData(prev => ({ ...prev, [field]: val })) },
            setFieldErrors,
            t
        );
        handleValidation({
            target: { name: 'tag', value }
        });
    };

    const isButtonDisabled = !!fieldErrors.tag || !tagValues || !finalName || updatingTag || toastTrigger || tagValues.name === finalName;

    return (
        <>
            <CustomToast
                {...tagToast}
                trigger={toastTrigger}
                handleActionsAfterToast={() => {
                    setToastTrigger(false);
                    hideEditTagPopup();
                    if (getSummaryCustomers) {
                        getSummaryCustomers(defMembership.business.id, pageOptions);
                    }
                    if (typeof getTags === "function") {
                        getTags(defMembership.business.id);
                    }
                    getTagGroupsGraphed(defMembership.business.id);
                    onTagsChanged?.();
                }}
                handleActionsAfterErrorToast={() => {
                    setShowErrorMessage(true);
                    setToastTrigger(false);
                }}
            />

            <Dialog
                header={t("tags.edit_tag")}
                visible={visible}
                style={{ width: "350px" }}
                id="tier_dialog"
                className="tier_dialog edit-tag-dialog"
                onHide={hideEditTagPopup}
            >
                <div className="p-grid p-fluid edit-tag-dialog-content">
                    <div className="edit_forms_div edit-tag-dialog-form">
                        <div className="label_inputs edit-tag-dialog-fields">
                            {!showCompactEdit && (
                                <>
                                    <label
                                        id='name_label'
                                        htmlFor="name"
                                        className="product-form-label">{t("tags.choose")}
                                    </label>
                                    <Dropdown
                                        options={tagGroupOptions}
                                        optionLabel='name'
                                        value={tagGroupValues}
                                        onChange={(e) => {
                                            setTagGroupValues(e?.value);
                                        }}
                                        placeholder={t("tags.select_tag_group")}
                                        className=" justify-content-center align-items-center tag-group-edit-dropdown dropdowns-style"
                                    />
                                    <div className='mt-4'>
                                        <label
                                            id='name_label'
                                            htmlFor="name"
                                            className="product-form-label">{t("tags.choose_tag")}
                                        </label>
                                        <div className='flex justify-content-flex-start align-items-center flex-direction-column '>
                                            <Dropdown
                                                value={tagValues}
                                                options={tagOptions}
                                                disabled={!tagGroupValues}
                                                optionLabel="name"
                                                placeholder={t("tags.select_tag")}
                                                className=" justify-content-center align-items-center tag-group-edit-dropdown large-tag-group-dropdown"
                                                onChange={(e) => {
                                                    setTagValues(e.value);
                                                    onDropdownChange(e);
                                                    setFinalName(e.value.name);
                                                    setShowErrorMessage(false)
                                                }}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                            {showCompactEdit && tagGroupValues?.name ? (
                                <div className="tags-dialog-tag-group-context mb-2">
                                    <span className="createdOnLabel">
                                        {t("tags.tag_group_label")}
                                        {":"}
                                    </span>
                                    <span className="name-body">{tagGroupValues.name}</span>
                                </div>
                            ) : null}
                                <div className={`grid footer-elements ${showCompactEdit ? "mt-1" : "mt-3"}`}>
                                    <div className='col-9'>
                                        <InputText
                                            value={finalName}
                                            onChange={handleInput}
                                            placeholder={t("tags.enter_tag_name")}
                                            disabled={!tagValues}
                                            maxLength={25}
                                            className='edit-tag-input'
                                        />
                                    </div>

                                    <div className='col-3'>
                                        <Button
                                            id="edit-tag-button"
                                            label={t("business_customers.update")}
                                            disabled={isButtonDisabled}
                                            className='save-btns'
                                            icon={updatingTag ? "pi pi-spinner pi-spin" : null}
                                            onClick={(data) => {
                                                onSubmit(data);
                                            }}
                                            autoFocus
                                            style={{
                                                position: 'relative'
                                            }}
                                        />
                                    </div>
                                </div>
                                {fieldErrors.tag && <p className="character_error_tags">{fieldErrors.tag}</p>}


                                {showErrorMessage ?
                                    <div className='mt-1'>
                                        <Trans
                                            i18nKey="tags.message_tag_name_exists"
                                            values={{
                                                tagName: finalName
                                            }}
                                            components={{
                                                1: <span className="tags_error_message" />,
                                                2: <span className="tags_error_message_bold" />,
                                                3: <span className="tags_error_message" />
                                            }}
                                        />
                                    </div>
                                    : null}
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    )
}